/** Structural edits of the itinerary as draft actions (plan §5): add, remove, move and
 *  move to another day / stop, for days, stops and activity rows. Each is ONE `SET` — one
 *  undo step — over the membership lists and the rows' own entries, with a label the
 *  undo button and the history read ("Move Golden Circle to Day 4").
 *
 *  The operations are the records outline's (`records/outline/ops.ts`) over the tour's
 *  levels (`TOUR_OUTLINE`) and membership lists (`MEMBERSHIP_PATHS`); this module names the
 *  rows in their labels and refusals, and adds what is the tour's own:
 *
 *  - add: a new stop takes the hour after the day's last stop;
 *  - restore: a stop goes back to the end of its day's list (the list is a set — a stop's
 *    place inside its day is its time); a day and an activity row at their stored place;
 *  - move: a day's position; an activity row's position in its stop; a stop or an
 *    activity row to another day / stop. A SAVED row moves only into a saved parent — the
 *    server keeps its id (and its documents, pin, guests' overrides) only between saved
 *    rows — otherwise the move is refused with the reason ("Save Day 5 first…").
 *
 *  Pure: they read the draft state and the baselines, and return the action. */
import { parseRecordKey, type DraftState, type RecordKey } from '@/features/records/draft/changeSet';
import {
  addRow,
  moveRow,
  moveRowTo,
  placeOf,
  removeRow,
  restoreRow,
  type OutlineOpResult,
  type OutlineOpsContext,
} from '@/features/records/outline/ops';
import { removedRows } from '@/features/records/outline/tree';

import { MEMBERSHIP_PATHS } from '../record/collections';

import {
  activityRec,
  DAY_SLOTS,
  dayRec,
  DAYS_PATH,
  hhmm,
  listIn,
  NEW_SLOT_TIME,
  newRowName,
  SLOT_ACTIVITIES,
  slotRec,
  TOUR_OUTLINE,
  valueIn,
  type Baselines,
} from './model';

export interface StructureNames {
  /** A catalog activity's name by id ("Cliffs of Moher entry"). */
  activityLabel: (id: unknown) => string;
}

export interface StructureContext {
  state: DraftState;
  baselines: Baselines;
  tourRec: RecordKey;
  names: StructureNames;
}

export type StructureResult = OutlineOpResult;

// ---------------------------------------------------------------------------
// Names in a label
// ---------------------------------------------------------------------------

const value = (ctx: StructureContext, rec: RecordKey, path: string) => valueIn(ctx.state.entries, ctx.baselines, rec, path);
const list = (ctx: StructureContext, rec: RecordKey, path: string) => listIn(ctx.state.entries, ctx.baselines, rec, path);
const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/** "Galway" (its title), else "Day 3". */
export function dayName(ctx: StructureContext, dayId: number): string {
  const title = str(value(ctx, dayRec(dayId), 'day.title'));
  if (title) return title;
  const i = list(ctx, ctx.tourRec, DAYS_PATH).indexOf(dayId);
  return i >= 0 ? `Day ${i + 1}` : dayId < 0 ? newRowName(dayId) : `Day #${dayId}`;
}

/** "Cliffs of Moher" (its title, else its first activity's name), else "the 10:00 stop". */
export function slotName(ctx: StructureContext, slotId: number): string {
  const rec = slotRec(slotId);
  const title = str(value(ctx, rec, 'slot.title'));
  if (title) return title;
  const first = list(ctx, rec, SLOT_ACTIVITIES)[0];
  if (first !== undefined) {
    const a = value(ctx, activityRec(first), 'activity.activity');
    if (typeof a === 'number') return ctx.names.activityLabel(a);
  }
  const time = str(value(ctx, rec, 'slot.time'));
  return time ? `the ${time} stop` : 'the stop';
}

export function activityName(ctx: StructureContext, activityId: number): string {
  const rec = activityRec(activityId);
  const display = str(value(ctx, rec, 'activity.display_name'));
  if (display) return display;
  const a = value(ctx, rec, 'activity.activity');
  return typeof a === 'number' ? ctx.names.activityLabel(a) : activityId < 0 ? 'the new activity' : 'the activity';
}

const dayNumber = (ctx: StructureContext, dayId: number) => list(ctx, ctx.tourRec, DAYS_PATH).indexOf(dayId) + 1;

// ---------------------------------------------------------------------------
// The records outline, bound to the tour
// ---------------------------------------------------------------------------

const DAY_LEVEL = 0;
const SLOT_LEVEL = 1;
const ACTIVITY_LEVEL = 2;

function ops(ctx: StructureContext): OutlineOpsContext {
  return { state: ctx.state, baselines: ctx.baselines, ownerRec: ctx.tourRec, spec: TOUR_OUTLINE, memberships: MEMBERSHIP_PATHS };
}

/** "HH:MM" an hour after the latest stop of a day (09:00 for an empty day). */
export function nextStopTime(ctx: StructureContext, dayId: number): string {
  let latest = '';
  for (const s of list(ctx, dayRec(dayId), DAY_SLOTS)) {
    const t = hhmm(str(value(ctx, slotRec(s), 'slot.time')));
    if (t > latest) latest = t;
  }
  if (!latest) return NEW_SLOT_TIME;
  const [h, m] = latest.split(':').map(Number) as [number, number];
  return `${String(Math.min(h + 1, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** The id of the row a stop or an activity row sits under in the draft (null: none). */
function parentOf(ctx: StructureContext, rec: RecordKey): number | null {
  const at = placeOf(ops(ctx), rec);
  return at ? parseRecordKey(at.parent).id : null;
}

// ---------------------------------------------------------------------------
// Days
// ---------------------------------------------------------------------------

export function addDay(ctx: StructureContext): StructureResult {
  const days = list(ctx, ctx.tourRec, DAYS_PATH);
  return addRow(ops(ctx), DAY_LEVEL, ctx.tourRec, { label: `Add Day ${days.length + 1}` });
}

export function removeDay(ctx: StructureContext, dayId: number): StructureResult | null {
  const days = list(ctx, ctx.tourRec, DAYS_PATH);
  const label = `Remove Day ${days.indexOf(dayId) + 1}${str(value(ctx, dayRec(dayId), 'day.title')) ? ` (${dayName(ctx, dayId)})` : ''}`;
  return removeRow(ops(ctx), dayRec(dayId), label);
}

export function moveDay(ctx: StructureContext, dayId: number, toIndex: number): StructureResult | null {
  return moveRow(ops(ctx), dayRec(dayId), toIndex, `Move ${dayName(ctx, dayId)} to Day ${toIndex + 1}`);
}

/** Its stops come back — except those the draft moved to another day meanwhile (a stop
 *  is in one day only). */
export function restoreDay(ctx: StructureContext, dayId: number): StructureResult {
  return restoreRow(ops(ctx), DAY_LEVEL, ctx.tourRec, dayId, { label: `Restore ${dayName(ctx, dayId)}` });
}

// ---------------------------------------------------------------------------
// Stops
// ---------------------------------------------------------------------------

export function addSlot(ctx: StructureContext, dayId: number): StructureResult {
  const time = nextStopTime(ctx, dayId);
  return addRow(ops(ctx), SLOT_LEVEL, dayRec(dayId), {
    label: `Add a stop to Day ${dayNumber(ctx, dayId)}`,
    ...(time !== NEW_SLOT_TIME ? { seed: { 'slot.time': time } } : {}),
  });
}

export function removeSlot(ctx: StructureContext, slotId: number): StructureResult | null {
  const dayId = parentOf(ctx, slotRec(slotId));
  if (dayId === null) return null;
  return removeRow(ops(ctx), slotRec(slotId), `Remove ${slotName(ctx, slotId)} from Day ${dayNumber(ctx, dayId)}`);
}

export function moveSlotToDay(ctx: StructureContext, slotId: number, toDayId: number): StructureResult | null {
  return moveRowTo(ops(ctx), slotRec(slotId), dayRec(toDayId), {
    label: `Move ${slotName(ctx, slotId)} to Day ${dayNumber(ctx, toDayId)}`,
    refused: `Save Day ${dayNumber(ctx, toDayId)} first — a saved stop moves only into a saved day (it keeps its activities’ documents and pin)`,
  });
}

/** A saved stop back into its stored day (which must be in the draft), at the end of the
 *  day's list. Its activity rows come back — except those moved to another stop meanwhile. */
export function restoreSlot(ctx: StructureContext, slotId: number, dayId: number): StructureResult {
  return restoreRow(ops(ctx), SLOT_LEVEL, dayRec(dayId), slotId, {
    label: `Restore ${slotName(ctx, slotId)}`,
    at: list(ctx, dayRec(dayId), DAY_SLOTS).length,
  });
}

// ---------------------------------------------------------------------------
// Activity rows
// ---------------------------------------------------------------------------

export function addActivity(ctx: StructureContext, slotId: number): StructureResult {
  return addRow(ops(ctx), ACTIVITY_LEVEL, slotRec(slotId), { label: `Add an activity to ${slotName(ctx, slotId)}` });
}

export function removeActivity(ctx: StructureContext, activityId: number): StructureResult | null {
  const slotId = parentOf(ctx, activityRec(activityId));
  if (slotId === null) return null;
  return removeRow(ops(ctx), activityRec(activityId), `Remove ${activityName(ctx, activityId)} from ${slotName(ctx, slotId)}`);
}

export function moveActivity(ctx: StructureContext, activityId: number, delta: -1 | 1): StructureResult | null {
  const slotId = parentOf(ctx, activityRec(activityId));
  if (slotId === null) return null;
  const index = list(ctx, slotRec(slotId), SLOT_ACTIVITIES).indexOf(activityId);
  return moveRow(ops(ctx), activityRec(activityId), index + delta, `Move ${activityName(ctx, activityId)} ${delta < 0 ? 'up' : 'down'}`);
}

export function moveActivityToSlot(ctx: StructureContext, activityId: number, toSlotId: number): StructureResult | null {
  return moveRowTo(ops(ctx), activityRec(activityId), slotRec(toSlotId), {
    label: `Move ${activityName(ctx, activityId)} to ${slotName(ctx, toSlotId)}`,
    refused: `Save ${slotName(ctx, toSlotId)} first — a saved activity row moves only into a saved stop (it keeps its documents)`,
  });
}

/** A saved activity row back into its stored stop, at its stored position. */
export function restoreActivity(ctx: StructureContext, activityId: number, slotId: number): StructureResult {
  return restoreRow(ops(ctx), ACTIVITY_LEVEL, slotRec(slotId), activityId, { label: `Restore ${activityName(ctx, activityId)}` });
}

// ---------------------------------------------------------------------------
// Removed saved rows (shown struck until the save)
// ---------------------------------------------------------------------------

/** Saved days the draft removed. */
export function removedDays(entries: DraftState['entries'] | null, baselines: Baselines, tourRec: RecordKey): number[] {
  return removedRows(TOUR_OUTLINE, entries, baselines, tourRec, DAY_LEVEL, new Set(listIn(entries, baselines, tourRec, DAYS_PATH)));
}

/** Saved stops of a day the draft removed (not moved to another day). */
export function removedSlots(
  entries: DraftState['entries'] | null,
  baselines: Baselines,
  dayId: number,
  placed: ReadonlySet<number>,
): number[] {
  return removedRows(TOUR_OUTLINE, entries, baselines, dayRec(dayId), SLOT_LEVEL, placed);
}

/** Saved activity rows of a stop the draft removed (not moved to another stop). */
export function removedActivities(
  entries: DraftState['entries'] | null,
  baselines: Baselines,
  slotId: number,
  placed: ReadonlySet<number>,
): number[] {
  return removedRows(TOUR_OUTLINE, entries, baselines, slotRec(slotId), ACTIVITY_LEVEL, placed);
}
